import { isEqual, uniqWith } from 'lodash';
import {
  PriceSubtotal,
  Discount as ComponentDiscount,
  PriceTotal,
} from '@vp/component-library';
import {
  AggregatedSubtotal,
  AggregatedTaxAwarePrice,
} from '@vp/om-tax-display';
import { MouseEventHandler } from 'react';
import { OrderSummaryMessages } from '../../../types/translations';
import { FeeSubtotal } from '../../../util/taxDisplayPricingHelpers';
import { Discount, OrderDetails } from '../../../types/orderDetails';

/**
 * Build shipping and item discounts with references to the same underlying discountSource object, which allows the @vp/omt-component-library DiscountPanel to de-duplicate
 * the same discount applied to both shipping and items. This is because the Javascript Set() -used by the DiscountPanel component- does a shallow equals rather than a deep comparison
 *
 * Eventually, the omt-component-library should be handling this deep comparison. {@see https://vistaprint.atlassian.net/browse/CRYPTID-1396}
 */
export const getDiscountSourceReferences = (
  iDiscounts: Discount[],
  sDiscounts: Discount[]
) => {
  const discountSources = uniqWith([
    iDiscounts,
    sDiscounts,
    isEqual,
  ]).flat() as Discount[];

  const itemDiscounts: Discount[] = [];
  const shippingDiscounts: Discount[] = [];

  discountSources.forEach(discount => {
    const { promotionId } = discount;
    if (
      iDiscounts.some(d => d.promotionId === promotionId) &&
      !itemDiscounts.find(a => a.promotionId === promotionId)
    ) {
      itemDiscounts.push(discount);
    }

    if (
      sDiscounts.some(d => d.promotionId === promotionId) &&
      !shippingDiscounts.find(a => a.promotionId === promotionId)
    ) {
      shippingDiscounts.push(discount);
    }
  });

  return { shippingDiscounts, itemDiscounts };
};

/**
 * Helper function to fetch all discount data for OrderSummary display
 */
export const getDiscountData = (order: OrderDetails, locale: string) => {
  const iDiscounts = order?.discounts?.itemDiscounts || [];
  const sDiscounts = order?.discounts?.shippingDiscounts || [];

  const { itemDiscounts, shippingDiscounts } = getDiscountSourceReferences(
    iDiscounts,
    sDiscounts
  );

  /* We only display one coupon in-line. Use the first discount in list for display because discounts are sorted by most relevant */
  const primaryItemDiscount =
    itemDiscounts?.length > 0 ? itemDiscounts[0] : undefined;
  const primaryShippingDiscount =
    shippingDiscounts?.length > 0 ? shippingDiscounts[0] : undefined;

  // only show itemDiscountModal if localization details present
  const showItemDiscountModal = !!(
    primaryItemDiscount?.details && primaryItemDiscount?.details[locale]
  );

  // only show shippingDiscountModal if localization details present
  const showShippingDiscountModal = !!(
    primaryShippingDiscount?.details && primaryShippingDiscount?.details[locale]
  );

  return {
    shippingDiscounts,
    itemDiscounts,
    primaryItemDiscount,
    primaryShippingDiscount,
    showShippingDiscountModal,
    showItemDiscountModal,
  };
};

export const getShippingLabel = (
  orderSummaryMessages: OrderSummaryMessages,
  order: OrderDetails,
  locale: string
): string => {
  const { shippingField } = orderSummaryMessages;
  const localizedShippingData = order?.orderShippingData?.localizedShippingData;

  if (localizedShippingData && localizedShippingData[locale]) {
    return `${shippingField}: ${localizedShippingData[locale].shippingMethodName}`;
  }

  const defaultShippingMethodName = order?.orderShippingData?.name;
  if (defaultShippingMethodName) {
    return `${shippingField}: ${defaultShippingMethodName}`;
  }

  return shippingField;
};

export const getFormattedOrderPrice = (
  taxAwareOrderTotalPrice: AggregatedTaxAwarePrice,
  rowLabel: string
): PriceTotal => {
  const {
    taxAwareListCents,
    taxAwareDiscountedCents,
    includedTaxes,
    excludedTaxes,
  } = taxAwareOrderTotalPrice;
  const formattedOrderPrice: PriceTotal = {
    listCents: taxAwareListCents,
    discountedCents: taxAwareDiscountedCents,
    rowLabel,
    includedTaxes,
    excludedTaxes,
  };
  return formattedOrderPrice;
};

export const getFormattedPriceSubtotal = (
  taxAwareTotalPrice: AggregatedSubtotal,
  rowLabel: string,
  discounts: ComponentDiscount[]
): PriceSubtotal => {
  const {
    taxAwareListCents,
    taxAwareDiscountedCents,
    includedTaxes,
    excludedTaxes,
  } = taxAwareTotalPrice;
  const formattedShippingPrice: PriceSubtotal = {
    listCents: taxAwareListCents,
    discountedCents: taxAwareDiscountedCents,
    rowLabel,
    includedTaxes,
    excludedTaxes,
    discounts,
  };
  return formattedShippingPrice;
};

export const getFormattedDiscounts = (
  taxAwareTotalPrice: AggregatedSubtotal,
  orderSummaryMessages: OrderSummaryMessages,
  onClick: MouseEventHandler,
  discount?: Discount
): ComponentDiscount[] => {
  if (!discount) {
    return [];
  }
  const centAmount =
    taxAwareTotalPrice.taxAwareListCents -
    taxAwareTotalPrice.taxAwareDiscountedCents;

  const componentDiscount: ComponentDiscount = {
    centAmount,
    label: orderSummaryMessages.savings,
    details: {
      type: discount.type,
      code: discount.couponCode,
    },
    onClick,
  };
  return [componentDiscount];
};

export const getFormattedPriceSubtotalFromFee = (
  fee: FeeSubtotal
): PriceSubtotal => ({
  includedTaxes: fee.includedTaxes,
  excludedTaxes: fee.excludedTaxes,
  discountedCents: fee.taxAwareDiscountedCents,
  listCents: fee.taxAwareListCents,
  rowLabelKey: fee.rowLabelKey,
  applicableItems: fee.applicableItems,
});
